/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { Container, TopHeadingGrid } from './style';
import { ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';
import GapBarChart from './GapBarChart';
import GapTabMenu from './GapTabMenu';
import RecommendationPopUp from './recommendpopup';
import GapRadarChart from './GapRadarChart';
import PageTabs from 'app/components/v2/PageTabs/PageTabs';
import { Buttons } from 'app/constants/sizing';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';

const GapAnalysis = () => {
  const [isFrameworksLoading, setIsFrameworksLoading] = useState(true);
  const [isSectionsLoading, setIsSectionsLoading] = useState(true);
  const [frameworks, setFrameworks] = useState([]);
  const [frameworkData, setFrameworkData] = useState({});
  const [frameworksStats, setFrameworksStats] = useState({});
  const [fwName, setFwName] = useState('');
  const [renderKey, setRenderKey] = useState(0);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedRecommendationSection, setselectedRecommendationSection] = useState('');
  const [curSection, setCurSection] = useState('');

  const { t } = useTranslation('translation', { keyPrefix: 'gap_analysis' });
  const language = JSON.parse(localStorage.getItem('lang'));

  let res = null;

    const fetchFrameworkStats = async () => {
      try {
        let res = null;
        res = await apiService.fetchFrameworksStats(language);
        // Framework statistics come in form of an array of objects
        // for easier usage we convert it to an object (id: {...framework statistics})
        // the id property is kept as a key inside as well as outside to make converting it back to an array seamless
        let newStats = {}
        res.data.frameworks.map((framework) => {
          newStats = {
            ...newStats,
            [framework.id]: {
              ...framework,
            }
          }
        });
        setFrameworksStats(newStats);
      }
      catch (error) {
        console.error(error);
      }
    };

  const fetchFrameworks = async () => {
    try {
      res = await apiService.fetchAllFrameworks(language);
      // Sort frameworks alphabetically
      const sortedFrameworks = res.data.frameworks.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
      setFrameworks(sortedFrameworks);
      setIsFrameworksLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSections = async frameworkId => {
    try {
      res = await apiService.fetchAllSections(frameworkId, language);
      setFwName(res.data?.name);
      setFrameworkData(prevData => ({
        ...prevData,
        [frameworkId]: {
          fwName: res.data?.name,
          ...prevData[frameworkId],
          ...(res.data?.sections && { allSections: res.data.sections }),
        },
      }));
      const defaultSection = res.data.sections[0].id;
      if (defaultSection) {
        setCurSection(defaultSection);
      }
      setIsSectionsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFrameworks();
    fetchFrameworkStats();
  }, []);

  const onReRender = questionId => {
    const newKey = questionId === renderKey ? `${questionId}Updated` : questionId;
    setRenderKey(newKey);
  };

  const onSelectFramework = async (value) => {
    if (value === '') return;
    if (value in frameworkData) {
      setSelectedModel(value);
      const defaultSection = Object.keys(frameworkData[value]?.sections)?.[0] || '';
      setCurSection(defaultSection);
      setFwName(frameworkData[value]?.fwName);
      setIsSectionsLoading(false);
      return;
    }
    setFrameworkData(prevData => ({
      ...prevData,
      [value]: {
        // fwName: '',
        sections: {
          // Section id:
          //    questions: [],
          //    answers: { questionId: {} }
        },
      },
    }));
    setSelectedModel(value);
    setIsSectionsLoading(true);
    await fetchSections(value);
  };

  const handleOpenRecommend = sectId => {
    setselectedRecommendationSection(sectId);
    // setShowRecommend(true);
  };
  const handleCloseRecommend = () => {
    setselectedRecommendationSection('');
    // setShowRecommend(false);
  };

  const handleResetSelection = () => {
    setSelectedModel('');
    setFwName('');
    setselectedRecommendationSection('');
  }

  return (
    <Container>
      <TopHeadingGrid>
        <Box sx={{ flexGrow: 1, paddingTop: '12px' }}>
          <Stack direction='row' spacing={1}>
              {!isFrameworksLoading && selectedModel === '' && 
              <Stack 
              direction='column' 
              alignItems='center'
              sx={{
                width: '100%',
              }}>
                <Stack 
                direction='column' 
                alignItems='center' 
                gap={4}
                sx={{ 
                  backgroundColor: ColorsV2.primary, 
                  padding: '20px' 
                }}>
                <Stack direction='row' alignItems={'center'} sx={{width: '100%'}}>
                  <Typography variant='h4' noWrap align='left' color={ColorsV2.white}>
                    {t('nic_gap_tool')}
                  </Typography>
                </Stack>
                <Typography variant='h5' noWrap sx={{ color: ColorsV2.textInfo, alignSelf: 'flex-start'}}>
                  {t('select_framework')}
                </Typography>
                {frameworks.length === 0 ? null : frameworks.map((framework) => {

                  const stats = frameworksStats?.[framework?.id] || {
                    nrOfAnswered: 0,
                    nrOfQuestions: 1,
                    sections: [],
                    description: '',
                  };
                  const percentage = Math.round((stats.nrOfAnswered / stats.nrOfQuestions) * 100);

                  return(
                  <Paper 
                  direction='column' 
                  key={framework?.id}
                  sx={{ 
                    backgroundColor: ColorsV2.secondary, 
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '10px',
                    border: '2px solid transparent',
                    borderRadius: '4px', 
                    width: '80vw',
                    maxWidth: '1200px',
                    cursor: 'pointer',
                    '&:hover': {
                      border: '2px solid' + ColorsV2.acent,
                    }
                  }}>
                    <Stack direction='column' sx={{ minWidth: '200px', padding: '10px' }} onClick={() => {onSelectFramework(framework?.id || '')}}>
                      <Typography variant='h6' sx={{ color: ColorsV2.white, }}>
                      {framework?.name}
                      </Typography>
                      <Stack direction='row' alignItems='center'>
                        <Typography variant='body1' sx={{ color: ColorsV2.outline, }}>
                          {percentage}%
                        </Typography>
                        <Divider flexItem orientation="vertical" sx={{ borderColor: ColorsV2.outline, marginX: '10px'}}></Divider>
                        <Typography variant='body1' sx={{ color: ColorsV2.textInfo, }}>
                          {stats.nrOfAnswered} / {stats.nrOfQuestions} {t('questions_answered')}
                        </Typography>
                        <Divider flexItem orientation="vertical" sx={{ borderColor: ColorsV2.outline, marginX: '10px'}}></Divider>
                        <Typography variant='body1' sx={{ color: ColorsV2.textInfo, }}>
                          {t('number_of_sections')}: {stats.sections?.length || 'unknown'}
                        </Typography>
                        <Divider flexItem orientation="vertical" sx={{ borderColor: ColorsV2.outline, marginX: '10px'}}></Divider>
                        <Typography variant='body1' sx={{ color: ColorsV2.textInfo, textWrap: 'wrap',}}>
                          {stats.description}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Box sx={{ backgroundColor: ColorsV2.primary, width: '100%', height: '440px', cursor: 'pointer', }}>
                      {frameworkData?.[framework?.id]?.name}
                      <GapRadarChart
                      fwId={framework?.id}
                      customOnClick={() => {onSelectFramework(framework?.id || '')}}
                      ></GapRadarChart>
                    </Box>
                  </Paper>
                )})}
                </Stack>
              </Stack>}
              <Stack direction='column' alignItems='flex-start'>
                {/* Implement a better loading placeholder */}
                {isSectionsLoading || selectedModel === '' ? (
                  !selectedModel === '' && <Typography sx={{ color: ColorsV2.white, }}>{t('loading')}</Typography>
                ) : (
                  <>
                    <Tooltip title={t('return_tooltip')}>
                      <IconButton
                        onClick={handleResetSelection}
                        sx={{
                          ...Buttons.baseStyle.iconButton,
                        }}
                      >
                        <KeyboardArrowLeftOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Stack       
                    direction='column'
                    justifyContent='flex-end'
                    alignItems='center'
                    sx={{
                      backgroundColor: ColorsV2.primary,
                      height: 340,
                      width: '100%',
                      overflow: 'visible',
                    }}>
                      <PageTabs
                      value={selectedModel}
                      onChange={(event, newValue) => onSelectFramework(newValue || '')}
                      indicatorColor={ColorsV2.acent}
                      indicatorPlacement='top'
                      tabs={frameworks.map((framework) => (
                        {
                          value: framework.id,
                          label: framework.name,
                          props: {
                            sx: {
                              backgroundColor: ColorsV2.secondary,
                              '&:hover': {
                                color: ColorsV2.acent,
                              },
                              "&.Mui-selected": {
                                backgroundColor: ColorsV2.primary
                              },
                            }
                          }
                        }
                      ))}
                      /> 
                      <Typography
                        variant='h4'
                        noWrap
                        color={ColorsV2.white}
                        sx={{
                          marginTop: '8px',
                          paddingBottom: '20px',
                        }}
                      >
                        {fwName}
                      </Typography>
                      <GapBarChart
                        fwId={selectedModel}
                        reRender={renderKey}
                      ></GapBarChart>
                    </Stack>
                    <Box sx={{ flexGrow: 1, paddingTop: '22px' }}>
                      <GapTabMenu
                        fwId={selectedModel}
                        frameworkData={frameworkData}
                        setFrameworkData={setFrameworkData}
                        sections={frameworkData[selectedModel].allSections}
                        onRecommend={handleOpenRecommend}
                        renderKey={renderKey}
                        onSliderChange={onReRender} // Prop drilling cheatsheet: onReRender -> onSliderChange -> onReRenderChartsAndRecommendations
                        curSection={curSection}
                        setCurSection={setCurSection}
                      ></GapTabMenu>
                    </Box>
                  </>
                )}
            </Stack>
          </Stack>
        </Box>
      </TopHeadingGrid>
      <RecommendationPopUp
        open={selectedRecommendationSection !== ''}
        handleClose={handleCloseRecommend}
        fwId={selectedModel}
        sectId={selectedRecommendationSection}
      ></RecommendationPopUp>
    </Container>
  );
};

export default GapAnalysis;
